import { Injectable, ElementRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { BaseFormatDatePipe } from '../pipes/base-format-date.pipe';
import { BaseModalService } from './base-modal.service';
import { BaseSigningStatusType, BaseSigningStatus } from '../../modules/assignments/_enums/base-signing-status.enum';
import { BaseDateService } from './base-date.service';
import { AssignmentPspResponse, PspAssignmentListItemResponse, ContractResponse } from '../../../api';
import { BaseCoreState, baseAssignmentsActions } from '../../_store';

interface CurrentOperationStatusInterface {
  signButtonTitle: string;
  statusButtonBorder: string;
  canBeSigned: boolean;
}

@Injectable({ providedIn: 'root' })
export class BaseEmployeeOperationService {
  constructor(
    private formatDatePipe: BaseFormatDatePipe,
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService
  ) {}

  updateStatus(
    assignment: AssignmentPspResponse | (PspAssignmentListItemResponse & { isSelected: boolean })
  ): CurrentOperationStatusInterface {
    let contract: ContractResponse;

    if ('contracts' in assignment) {
      contract = assignment.contracts?.find((contract) => contract.valid) as ContractResponse;
    } else {
      contract = (assignment as PspAssignmentListItemResponse).contract;
    }

    return {
      canBeSigned:
        contract?.signatures?.statusPsp !== 'signed' &&
        contract?.signingStatus !== BaseSigningStatus.signed_by_psp &&
        contract?.signingStatus !== BaseSigningStatus.signed,
      statusButtonBorder: this.setSignButtonClass(contract?.signingStatus),
      signButtonTitle: this.setSignButtonTitle(contract?.signingStatus),
    };
  }

  checkAssigmentDate(
    assignment: AssignmentPspResponse | (PspAssignmentListItemResponse & { isSelected: boolean })
  ): boolean {
    const date = this.formatDatePipe.transform(new Date());
    const formattedDate = BaseDateService.toServerFormatString(date);
    return BaseDateService.isEqual(new Date(formattedDate), new Date(assignment.endDate));
  }

  formatAgreedHours(hours: number): string {
    const [integer, fraction] = hours.toFixed(2).split('.');
    const decimals = fraction.replace(/0+$/, '');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return decimals ? `${grouped},${decimals}` : grouped;
  }

  showEmployeeModal(employeeModal: ElementRef, assignmentId: number) {
    this.store.dispatch(baseAssignmentsActions.loadAssignment({ payload: { assignment: assignmentId } }));
    this.store.dispatch(baseAssignmentsActions.loadAssignmentDates({ payload: { assignment: assignmentId } }));

    return this.modalService.open(
      {
        hideHeader: true,
        bodyTemplate: employeeModal,
        hideFooter: true,
      },
      {
        size: 'xl',
      }
    );
  }

  setSignButtonTitle(signingStatus: BaseSigningStatusType): string {
    return signingStatus === BaseSigningStatus.signed
      ? 'candidates.status.signed'
      : signingStatus === BaseSigningStatus.signed_by_psp
      ? 'externalEmployees.contractStatus.waitingForPartner'
      : signingStatus === BaseSigningStatus.unsigned || signingStatus === BaseSigningStatus.signed_by_customer
      ? 'externalEmployees.contractStatus.signatureRequired'
      : '';
  }

  setSignButtonClass(signingStatus: BaseSigningStatusType): string {
    return signingStatus === BaseSigningStatus.signed
      ? 'state-btn-border-green'
      : signingStatus === BaseSigningStatus.signed_by_psp
      ? 'state-btn-border-yellow'
      : signingStatus === BaseSigningStatus.unsigned || signingStatus === BaseSigningStatus.signed_by_customer
      ? 'state-btn-border-red'
      : '';
  }
}
